import { useState, useEffect } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { Row, Col, Button, notification, Spin } from "antd";
import { shallowEqual, useSelector } from "react-redux";
import { WhatsAppOutlined, MessageOutlined } from "@ant-design/icons";
import "./whatsAppMessageTrigger.css";

// Custom Components & Values Block
import { getSettingKey, handleApiErrorResponse, toAbsoluteUrl } from "../../../../../Common/utils";
import PhoneInput from "./../../../../../Common/partials/PhoneInput";
import { Input } from "../../../../../../../_metronic/_partials/controls";
import {
  sendInvoiceOnWhatsAppMessage,
  sendInvoiceOnSmsMessage,
} from "../../../../../Marketing/_redux/promotions/promotionsCrud";

export default function WhatsAppMessageTrigger({ currentOrder, order }) {
  const [sendWhatsAppMessageLoader, setSendWhatsAppMessageLoader] = useState(false);
  const [sendSmsLoader, setSendSmsLoader] = useState(false);
  const { settingsState } = useSelector((state) => ({ settingsState: state.basicSettings }), shallowEqual);
  const { entity } = settingsState;
  const [dialCode, setDialCode] = useState("");

  // react-phone-input-2 expects `value` as digits-only string.
  // Cast to string first because some APIs may return numbers.
  const normalizePhone = (value) => String(value || "").replace(/[^0-9]/g, "");

  // Timer to blink the "New" Tag
  const [showNewTag, setShowNewTag] = useState(true);

  useEffect(() => {
    const oneMonthInMilliseconds = 30 * 24 * 60 * 60 * 1000;
    const expirationDate = new Date(Date.now() + oneMonthInMilliseconds);

    const intervalId = setInterval(() => {
      const now = new Date();
      if (now > expirationDate) {
        setShowNewTag(false);
        clearInterval(intervalId);
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  const isValidPhone = (value) => normalizePhone(value).length >= 10;

  const sendWhatsAppMessage = async (values) => {
    try {
      setSendWhatsAppMessageLoader(true);
      const rawMobile = String(values.mobile_number || "").trim();
      const payload = {
        ...values,
        mobile_number: rawMobile ? (rawMobile.startsWith("+") ? rawMobile : `+${rawMobile}`) : "",
      };
      await sendInvoiceOnWhatsAppMessage(payload);
      notification.success({
        message: "Invoice successfully sent via WhatsApp!",
      });
    } catch (error) {
      notification.error({
        message: "Failed to send the invoice via WhatsApp. Please try again later.",
      });
    } finally {
      setSendWhatsAppMessageLoader(false);
    }
  };

  const sendSmsMessage = async (phone, orderData) => {
    const rawPhone = String(phone || "").trim();
    const phoneWithPlus = rawPhone ? (rawPhone.startsWith("+") ? rawPhone : `+${rawPhone}`) : "";
    const phoneDigits = normalizePhone(rawPhone);

    if (phoneDigits && !isValidPhone(phoneDigits)) {
      notification.warning({ message: "Please enter a valid phone number." });
      return;
    }
    // `orderData` can be null/blank during async render; fall back to props.
    const saleOrderId = orderData?.id || currentOrder?.id || order?.id;
    if (!saleOrderId) {
      notification.error({ message: "Sale order id is missing. Please try again." });
      return;
    }
    const smsPayload = {
      sale_order_id: saleOrderId,
      // Backend uses `request_phone or customer_phone`, so empty string will trigger fallback.
      phone_number: phoneWithPlus || "",
    };

    try {
      setSendSmsLoader(true);
      const response = await sendInvoiceOnSmsMessage(smsPayload);
      notification.success({
        message: response?.data?.message || "Invoice successfully sent via SMS!",
      });
    } catch (error) {
      handleApiErrorResponse(error);
    } finally {
      setSendSmsLoader(false);
    }
  };

  return (
    <Formik
      enableReinitialize={true}
      initialValues={{
        mobile_number: normalizePhone(
          currentOrder?.customer?.business_phone_no || order?.customer?.business_phone_no || ""
        ),
        sale_order_id: currentOrder?.id || order?.id,
        template_name: "khub_sale_order",
      }}
      validationSchema={() =>
        Yup.lazy(() => {
          return Yup.object().shape({
            mobile_number: Yup.string().required().label("WhatsApp Number"),
          });
        })
      }
      onSubmit={(values) => {
        sendWhatsAppMessage(values);
      }}
    >
      {({ setFieldValue, setTouched, values, handleSubmit }) => (
        <Form>
          <Spin spinning={sendWhatsAppMessageLoader || sendSmsLoader}>
            <Row className="whatsapp-trigger-row">
              <Col xl={11} lg={11} md={24} sm={24} xs={24} className="mt-4 whatsapp-phone-col mr-3">
                <div className="position-relative">
                  {showNewTag && (
                    <span className="new-tag">
                      <img height={30} src={toAbsoluteUrl("/media/svg/shapes/new_tag.gif")} />
                    </span>
                  )}
                  <PhoneInput
                    country={getSettingKey(entity, "Basic", "country_iso2")}
                    value={values.mobile_number}
                    onChange={(value, data) => {
                      setDialCode(data.dialCode);
                      setTouched({ mobile_number: true });
                      setFieldValue("mobile_number", value);
                    }}
                  />
                </div>
                <Field
                  className="d-none"
                  name="mobile_number"
                  component={Input}
                  autoFocus={false}
                  value={values.mobile_number}
                  customFeedbackLabel=" "
                  placeholder="Phone Number (xxx)-xxx-xxxx"
                />
              </Col>
              <Col xl={12} lg={12} md={24} sm={24} xs={24} className="mt-4 d-flex align-items-start whatsapp-actions-col whatsapp-actions-col-gap">
                <div className="d-flex align-items-center whatsapp-actions-group whatsapp-actions-group-gap">
                  <Button
                    onClick={() => sendSmsMessage(values.mobile_number, currentOrder || order)}
                    className="whatsapp-send-sms-btn"
                  >
                    <span className="d-flex align-items-center whatsapp-action-gap">
                      Send via
                      <span>
                        <MessageOutlined className="whatsapp-action-icon" />
                      </span>
                    </span>
                  </Button>
                  <div className="whatsapp-actions-divider" />
                  <Button
                    onClick={handleSubmit}
                    className="whatsapp-send-whatsapp-btn"
                  >
                    <span className="d-flex align-items-center whatsapp-action-gap">
                      Whatsapp
                      <span>
                        <WhatsAppOutlined className="whatsapp-action-icon" />
                      </span>
                    </span>
                  </Button>
                </div>
              </Col>
            </Row>
          </Spin>
        </Form>
      )}
    </Formik>
  );
}
